import type { ReactNode } from 'react';
import CloseIcon from 'svg/CloseIcon';
import PaperPlane from 'svg/PaperPlane';
import { Button } from 'components/Button';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onRequestToPlay?: () => void;
  /**
   * When the viewer already has a live request, this card renders at the end of
   * the scrolling body (with a divider) in place of the "Request to play" footer.
   */
  activeRequest?: ReactNode;
  /**
   * A custom pinned footer (e.g. the host's Cancel/Edit buttons). When provided
   * it replaces the default "Request to play" footer, and the `activeRequest`
   * end-of-body card is skipped — the caller owns the footer entirely.
   */
  footer?: ReactNode;
  /** Hides the default "Request to play" footer entirely — e.g. the viewer owns
   *  this listing, so there's nothing to request. */
  hideRequestFooter?: boolean;
  /** Heading pinned in the header. Without one the header is just the X. */
  title?: string;
  /** `lg`: Figma's "Modal M" from lg up: 640px wide with roomier header, body and footer. */
  sizeDesktop?: 'lg';
  /** A rule above the footer at every breakpoint, for modals whose actions are divided off. */
  hasFooterDivider?: boolean;
  /** A rule above the footer from lg up, with the footer inset to the body (the host's actions). */
  hasFooterDividerAtDesktop?: boolean;
  /** Scrollable content between the pinned header and footer. */
  children: ReactNode;
}

/** The capped bottom-sheet shell shared by the game- and player-detail modals:
 *  pinned header (an X, plus an optional `title` over a rule), scrolling body, and
 *  either a caller-supplied `footer`, the default "Request to play" footer, or
 *  (once a request exists) the "Your request" card at the end of the body. */
export default function DetailModalShell({
  isOpen,
  onClose,
  onRequestToPlay,
  activeRequest,
  footer,
  hideRequestFooter,
  title,
  sizeDesktop,
  hasFooterDivider = false,
  hasFooterDividerAtDesktop = false,
  children,
}: Props) {
  const isLargeAtDesktop = sizeDesktop === 'lg';
  const footerClass = classNames(
    'shrink-0 px-ds-lg pb-ds-lg pt-ds-lg',
    hasFooterDivider &&
      'border-t border-color-border-input-lightmode dark:border-color-border-input-darkmode',
    isLargeAtDesktop && 'lg:py-5',
    isLargeAtDesktop && !hasFooterDividerAtDesktop && 'lg:px-ds-xl',
    hasFooterDividerAtDesktop &&
      'lg:border-t lg:border-color-border-input-lightmode lg:px-ds-3xl lg:dark:border-color-border-input-darkmode',
  );

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onClose}
      classNameMaxWidth={classNames('max-w-md', isLargeAtDesktop && 'lg:max-w-[40rem]')}
      classNameRounded={
        isLargeAtDesktop ? 'rounded-t-3xl sm:rounded-2xl lg:rounded-3xl' : undefined
      }
      classNameHeight="max-h-[90dvh]"
    >
      <div className="flex max-h-[90dvh] flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
        {/* Header and footer stay pinned; only the content between them scrolls. */}
        <header
          className={classNames(
            'flex shrink-0 items-center p-ds-lg',
            title
              ? 'justify-between gap-ds-md border-b border-color-border-input-lightmode dark:border-color-border-input-darkmode'
              : 'justify-end',
            isLargeAtDesktop && 'lg:px-ds-3xl lg:py-5',
          )}
        >
          {title && (
            <h2 className="typography-product-heading-mobile truncate text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {title}
            </h2>
          )}

          <Button
            variant="secondary"
            size="icon"
            isInline
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 rounded-full border-none bg-transparent lg:w-8"
            iconLeft={<CloseIcon className="h-3.5 w-3.5" />}
          />
        </header>

        <div
          className={classNames(
            'min-h-0 flex-1 overflow-y-auto px-ds-lg pb-ds-lg',
            // The untitled header's own padding already spaces the body; a titled
            // one ends in a rule, so the body has to open its own gap.
            title && 'pt-ds-lg',
            isLargeAtDesktop && 'lg:px-ds-3xl lg:pb-ds-3xl',
          )}
        >
          {children}

          {/* The end-of-body "Your request" card is a default-footer concern only;
              a caller-supplied `footer` owns the bottom of the sheet outright. */}
          {!footer && activeRequest && (
            <>
              <div className="mt-10 h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
              <div className="mt-ds-lg">{activeRequest}</div>
            </>
          )}
        </div>

        {footer ? (
          <footer className={footerClass}>{footer}</footer>
        ) : (
          !activeRequest &&
          !hideRequestFooter && (
            <footer className={footerClass}>
              <Button
                variant="brand"
                size="lg"
                iconLeft={
                  <PaperPlane className={classNames('h-5 w-5', isLargeAtDesktop && 'lg:hidden')} />
                }
                onClick={onRequestToPlay}
              >
                Request to play
              </Button>
            </footer>
          )
        )}
      </div>
    </Modal>
  );
}
